<template>
  <app-modal :modal-in="modelValue" @close="closeModal" :dynamic="true" :breakpoint="1200" modal-class="modal-xl">
    <template #header>
      {{ currentItem.title }}
    </template>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="skuItems.length > 0" class="container-fluid">
      <!-- Missing SKU Warning — only relevant when store uses SKUs as unique identifiers -->
      <div v-if="uniqueSkuMode && !currentItem.SKU" class="alert alert-warning alert-dismissible fade show mb-3" role="alert">
        <strong>Missing SKU:</strong> This item doesn't have a SKU assigned.
        History tracking across multiple listings is limited to this single listing.
        Add a SKU in eBay and re-sync to enable full history and continuity tracking.
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>

      <div class="row">
        <!-- Image Gallery Column -->
        <div class="col-lg-5" v-if="lean===false">
          <template v-if="!showAddImage">
            <image-gallery
              v-if="galleryImages.length > 0"
              :images="galleryImages"
              :auto-play="false"
              class="border rounded overflow-hidden mb-3"
            />
            <div v-else class="bg-light border rounded p-4 text-center text-muted mb-3">
              No images available
            </div>
            <div v-if="isCustomItem" class="text-end mb-3">
              <a href="#" class="small" @click.prevent="showAddImage = true">
                <i class="bi me-1" :class="galleryImages.length > 0 ? 'bi-arrow-repeat' : 'bi-plus-circle'"></i>
                {{ galleryImages.length > 0 ? 'Change image' : 'Add image' }}
              </a>
            </div>
          </template>
          <div v-else-if="isCustomItem" class="bg-light border rounded p-3 mb-3">
            <image-source-picker
              :request-upload-url="requestInventoryUploadUrl"
              :on-resolved="onImageResolved"
              @saved="showAddImage = false"
              @cancel="showAddImage = false"
            />
          </div>

          <!-- Quick Stats TODO: Make Meaningful -->
          <div v-if="false" class="card mb-3">
            <div class="card-body">
              <h6 class="card-title">Summary</h6>
              <div class="row g-2 small">
                <div class="col-6">
                  <div class="text-muted">Total Listings:</div>
                  <div class="fw-bold">{{ skuItems.length }}</div>
                </div>
                <div class="col-6">
                  <div class="text-muted">Active:</div>
                  <div class="fw-bold">{{ activeCount }}</div>
                </div>
                <div class="col-6">
                  <div class="text-muted">Total Orders:</div>
                  <div class="fw-bold">{{ allOrders.length }}</div>
                </div>
                <div class="col-6">
                  <div class="text-muted">Completed:</div>
                  <div class="fw-bold text-success">{{ completedOrders.length }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Edit Section -->
          <item-source-edit
            :item="currentItem"
            class="mb-3"
            @update:source-price="onSourceUpdated"
            @update:acquisition-date="onSourceUpdated"
            @update:source-data="onSourceUpdated"
          />
        </div>

        <!-- History & Details Column -->
        <div :class="{'col-lg-12': lean===true, 'col-lg-7':lean===false}">
          <!-- Comprehensive Timeline -->
          <div class="card mb-3">
            <div class="card-body">
              <h5 class="card-title">Item Timeline</h5>

              <div class="timeline">
                <div
                  v-for="(event, index) in timelineEvents"
                  :key="`event-${index}`"
                  class="timeline-item"
                  :class="`${event.event_type}-event`"
                >
                  <div class="timeline-marker">
                    <i class="bi" :class="getEventIcon(event.event_type)"></i>
                  </div>
                  <div class="timeline-content">
                    <div
                      class="d-flex justify-content-between align-items-start"
                      :class="{ 'mb-2': expandedEvents[index] }"
                      style="cursor: pointer"
                      @click="toggleEvent(index)"
                    >
                      <div>
                        <strong>{{ formatDate(event.event_date) }}</strong>
                        <span class="text-muted ms-2">({{ getEventLabel(event.event_type) }})</span>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <span class="badge" :class="getEventBadgeClass(event.event_type)">
                          {{ event.event_type.toUpperCase() }}
                        </span>
                        <i class="bi small" :class="expandedEvents[index] ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                      </div>
                    </div>

                    <Transition name="event-details">
                    <table v-show="expandedEvents[index]" class="table table-sm table-borderless mb-0">
                      <tbody>
                        <!-- Acquisition Event -->
                        <template v-if="event.event_type === 'acquisition'">
                          <tr>
                            <td class="text-muted" style="width: 140px">Source Price:</td>
                            <td class="text-danger fw-bold">${{ event.event_data.source_price || '0.00' }}</td>
                          </tr>
                          <tr v-if="event.event_data.source_category">
                            <td class="text-muted">Source Category:</td>
                            <td>{{ event.event_data.source_category }}</td>
                          </tr>
                        </template>

                        <!-- Listing/Relist Event -->
                        <template v-if="['listing', 'relist'].includes(event.event_type)">
                          <tr>
                            <td class="text-muted" style="width: 140px">Remote ID:</td>
                            <td>
                              <a :href="`https://www.ebay.com/itm/${event.event_data.remote_id}`" target="_blank" class="text-decoration-none">
                                {{ event.event_data.remote_id }}
                                <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                              </a>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted">Sale Price:</td>
                            <td class="fw-bold text-success">${{ event.event_data.sale_price }}</td>
                          </tr>
                          <tr v-if="event.event_data.condition">
                            <td class="text-muted">Condition:</td>
                            <td>{{ event.event_data.condition }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Status:</td>
                            <td>
                              <span class="badge" :class="event.event_data.is_active ? 'bg-success' : 'bg-secondary'">
                                {{ event.event_data.is_active ? 'Active' : 'Ended' }}
                              </span>
                            </td>
                          </tr>
                        </template>

                        <!-- Sale/Cancellation/Return Event -->
                        <template v-if="['sale', 'cancellation', 'return'].includes(event.event_type)">
                          <tr>
                            <td class="text-muted" style="width: 140px">Order ID:</td>
                            <td>
                              <order-id-link :order-id="event.event_data.order_id" />
                            </td>
                          </tr>
                          <tr>
                            <td class="text-muted">Amount:</td>
                            <td class="fw-bold">${{ event.event_data.amount_paid?.toFixed(2) || '0.00' }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Platform:</td>
                            <td>{{ event.event_data.remote_store }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Quantity:</td>
                            <td>{{ event.event_data.quantity }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Status:</td>
                            <td>
                              <span class="badge" :class="getOrderStatusBadgeClass(event.event_data.order_status)">
                                {{ event.event_data.order_status }}
                              </span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                    </Transition>
                  </div>
                </div>
              </div>
              <!-- Non-unique mode: FYI when other listings share this Custom Label (SKU) -->
              <div v-if="uniqueSkuMode && skuItems.length > 1" class="card-body small">
                Grouped with {{ skuItems.length - 1 }} other listings with this SKU.
                History, images, and relists are shared.
                <a href="#" @click.prevent="goToSkuSearch(currentItem.SKU)">
                  [See all items with this SKU]
                </a>
              </div>
              <div v-if="!uniqueSkuMode && currentItem.SKU && skuCount !== null && skuCount > 1" class="card-body small text-muted border-top pt-3 mt-2">
                <i class="bi bi-tag me-1"></i>
                {{ skuCount - 1 }} other {{ skuCount - 1 === 1 ? 'listing' : 'listings' }} also use Custom Label (SKU) <code>{{ currentItem.SKU }}</code>.
                <a href="#" @click.prevent="goToSkuSearch(currentItem.SKU)">
                  [See all items with this SKU]
                </a>
              </div>

            </div>

          </div>

          <!-- All Orders Across SKU -->
          <div v-if="lean===false && allOrders.length > 0" class="card mb-3">
            <div class="card-body">
              <h5 class="card-title">All Orders for {{ currentItem.SKU ? `SKU ${currentItem.SKU}` : 'this Item' }}</h5>
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Platform</th>
                      <th>Qty</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="order in allOrders" :key="order.order_id">
                      <td>
                        <order-id-link :order-id="order.order_id" />
                      </td>
                      <td class="small">{{ formatDate(order.order_date) }}</td>
                      <td>
                        <span class="badge" :class="getOrderStatusBadgeClass(order.order_status)">
                          {{ order.order_status }}
                        </span>
                      </td>
                      <td class="small">{{ order.remote_store || 'N/A' }}</td>
                      <td>{{ order.quantity }}</td>
                      <td>${{ order.amount_paid?.toFixed(2) || '0.00' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Item Details -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title me-auto">Item Details</h5>
              <div class="d-flex flex-wrap gap-2 align-items-center justify-content-end">
                <span v-if="hasActiveListing" class="badge bg-success text-white">ACTIVE</span>
                <span v-if="hasSoldItems" class="badge bg-danger text-white">SOLD</span>
              </div>

              <table class="table table-sm">
                <tbody>
                <tr>
                  <td class="text-muted">List Price:</td>
                  <td>
                    <money-block :dollar="item.sale_price" />
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Available QTY::</td>
                  <td>
                    {{ currentItem.available_quantity }}
                  </td>
                </tr>
                  <tr>
                    <td class="text-muted">SKU:</td>
                    <td>
                      <span v-if="currentItem.SKU">{{ currentItem.SKU }}</span>
                      <span v-else class="text-muted fst-italic">Not assigned</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">eBay Category:</td>
                    <td><ebay-category :category-id="currentItem.ebay_category_id" /></td>
                  </tr>
                  <tr v-if="currentItem.condition_name">
                    <td class="text-muted" style="width: 180px">Condition:</td>
                    <td>
                      {{ currentItem.condition_name }}
                      <span v-if="currentItem.condition_description" class="text-muted small d-block">
                        {{ currentItem.condition_description }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="hasShippingInfo">
                    <td class="text-muted">Weight:</td>
                    <td>{{ currentItem.weight_lbs || 0 }} lbs {{ currentItem.weight_ozs || 0 }} oz</td>
                  </tr>
                  <tr v-if="hasShippingInfo">
                    <td class="text-muted">Dimensions:</td>
                    <td>
                      {{ currentItem.package_length }}" × {{ currentItem.package_width }}" × {{ currentItem.package_depth }}"
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="currentItem.description" class="mt-3">
                <h6>Description</h6>
                <div class="description-content small" v-html="currentItem.description"></div>
              </div>

              <div class="text-end mt-3" v-if="lean===false">
                <update-inventory-item-button :item-id="item.remote_id" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="p-5"></div>
  </app-modal>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { getInventoryGalleryUrls } from '@/composables/useInventoryImages'
import AppModal from '@/app/components/Utils/AppModal.vue'
import ImageGallery from '@/app/components/Utils/ImageGallery.vue'
import ImageSourcePicker from '@/app/components/Utils/ImageSourcePicker.vue'
import ItemSourceEdit from '@/app/components/inventory/ItemSourceEdit.vue'
import OrderIdLink from "@/app/components/orders/OrderIdLink.vue";
import UpdateInventoryItemButton from "@/app/components/inventory/UpdateInventoryItemButton.vue";
import EbayCategory from "@/app/components/ebay/EbayCategory.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  lean: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])
const store = useStore()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const skuItems = ref([])
const timelineEvents = ref([])
const expandedEvents = ref({})
const skuCount = ref(null)
const showAddImage = ref(false)

// Whether this store treats SKU as a unique identifier
const uniqueSkuMode = computed(() => store.state.eshop.eshop?.config?.UNIQUE_SKU === 'true')

// Fetch all items and timeline when modal opens
watch(() => props.modelValue, async (newVal) => {
  if (newVal) {
    loading.value = true
    skuCount.value = null
    showAddImage.value = false
    try {
      const { items, timeline } = await store.dispatch('eshop/fetchInventoryDetailData', { item: props.item })
      skuItems.value = items
      timelineEvents.value = timeline
      // In non-unique mode, fetch how many listings share this Custom Label (SKU) as an FYI
      if (!uniqueSkuMode.value && props.item.SKU) {
        store.dispatch('eshop/fetchSkuCount', { sku: props.item.SKU })
          .then(count => { skuCount.value = count })
          .catch(() => { skuCount.value = 0 })
      }
    } catch (error) {
      console.error('Failed to fetch inventory data:', error)
      skuItems.value = [props.item]
      timelineEvents.value = []
    } finally {
      loading.value = false
    }
  }
}, { immediate: true })

// Use the most recent (first) item as current
const currentItem = computed(() => skuItems.value[0] || props.item)

// Get all gallery images from the current item
const galleryImages = computed(() => {
  return getInventoryGalleryUrls(currentItem.value, 'large')
})

// eBay-synced items are read-only for images in ESC — eBay is the source of truth.
// Only custom items (remote_id prefixed 'CUSTOM-', see inventory_resync_job.js) can have images added/changed here.
const isCustomItem = computed(() => String(currentItem.value.remote_id || '').startsWith('CUSTOM-'))

// Active listings
const activeListings = computed(() => {
  return skuItems.value.filter(item => isItemActive(item))
})

const activeCount = computed(() => {
  return activeListings.value.reduce((acc, item) => acc + item.available_quantity, 0)
})

// Check if there's any active listing
const hasActiveListing = computed(() => activeListings.value.length > 0)

// Get all orders across all listings
const allOrders = computed(() => {
  const orders = []
  skuItems.value.forEach(item => {
    if (item.orders && Array.isArray(item.orders)) {
      item.orders.forEach(order => {
        orders.push(order)
      })
    }
  })
  // Sort by date descending
  return orders.sort((a, b) => new Date(b.order_date) - new Date(a.order_date))
})

// Completed orders
const completedOrders = computed(() => {
  return allOrders.value.filter(o => o.order_status === 'Completed' || o.order_status === 'Complete')
})

// Check if any items have been sold
const hasSoldItems = computed(() => allOrders.value.length > 0)

// Check if shipping info exists
const hasShippingInfo = computed(() => {
  return (currentItem.value.weight_lbs || currentItem.value.weight_ozs) &&
         (currentItem.value.package_length && currentItem.value.package_width && currentItem.value.package_depth)
})

function closeModal() {
  emit('update:modelValue', false)
}

function goToSkuSearch(sku) {
  emit('update:modelValue', false)
  router.push({ name: 'Inventory', params: { storeKey: route.params.storeKey }, query: { search: sku } })
    .catch(() => {})
}

function toggleEvent(index) {
  expandedEvents.value[index] = !expandedEvents.value[index]
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function isItemActive(item) {
  const qty = parseInt(item.available_quantity) || 0
  return qty > 0
}

function getOrderStatusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'complete':
      return 'bg-success'
    case 'cancelled':
    case 'canceled':
      return 'bg-danger'
    case 'active':
      return 'bg-primary'
    case 'returned':
      return 'bg-warning'
    default:
      return 'bg-secondary'
  }
}

function getEventIcon(eventType) {
  const icons = {
    acquisition: 'bi-cart-plus-fill text-primary',
    listing: 'bi-tag-fill text-success',
    relist: 'bi-arrow-repeat text-info',
    sale: 'bi-cash-coin text-success',
    cancellation: 'bi-x-circle-fill text-danger',
    return: 'bi-arrow-return-left text-warning'
  }
  return icons[eventType] || 'bi-circle'
}

function getEventLabel(eventType) {
  const labels = {
    acquisition: 'Acquired',
    listing: 'Listed',
    relist: 'Relisted',
    sale: 'Sold',
    cancellation: 'Cancelled',
    return: 'Returned'
  }
  return labels[eventType] || eventType
}

function getEventBadgeClass(eventType) {
  const classes = {
    acquisition: 'bg-primary',
    listing: 'bg-success',
    relist: 'bg-info',
    sale: 'bg-success',
    cancellation: 'bg-danger',
    return: 'bg-warning'
  }
  return classes[eventType] || 'bg-secondary'
}

function requestInventoryUploadUrl(file) {
  return store.dispatch('eshop/getInventoryImageUploadUrl', {
    remoteId: currentItem.value.remote_id,
    contentType: file.type
  })
}

async function onImageResolved(url) {
  await store.dispatch('eshop/updateInventoryImage', {
    ...currentItem.value,
    photo_url: url,
    photo_gallery: [url]
  })
  await onSourceUpdated()
}

// Handler for when source data is updated in the child component
async function onSourceUpdated() {
  try {
    const { items } = await store.dispatch('eshop/fetchInventoryDetailData', { item: props.item })
    skuItems.value = items
  } catch (error) {
    console.error('Failed to refresh inventory data:', error)
  }
}

</script>

<style lang="scss" scoped>
@import '@/assets/sass/_variables-esc.scss';

.description-content {
  max-height: 200px;
  overflow-y: auto;

  :deep(img) {
    max-width: 100%;
    height: auto;
  }
}

.timeline {
  position: relative;
  padding-left: 30px;

  &::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: $gray-300;
  }
}

.timeline-item {
  position: relative;
  padding-bottom: 2rem;

  &:last-child {
    padding-bottom: 0;
  }

  &.active-listing {
    .timeline-content {
      background: lighten($info, 45%);
      border-left: 3px solid $info;
    }
  }

  // Acquisition: Orange tones (purchase phase)
  &.acquisition-event {
    .timeline-content {
      background: lighten($brand-goldenrod, 35%);
      border-left: 3px solid $brand-goldenrod;
    }
  }

  // Listing: Blue tones (listed/active phase)
  &.listing-event {
    .timeline-content {
      background: lighten($info, 45%);
      border-left: 3px solid $info;
    }
  }

  // Relist: Lighter blue (re-listed phase)
  &.relist-event {
    .timeline-content {
      background: lighten($brand-teal, 50%);
      border-left: 3px solid $brand-teal;
    }
  }

  // Sale: Green tones (successful sale phase)
  &.sale-event {
    .timeline-content {
      background: lighten($success, 45%);
      border-left: 3px solid $success;
    }
  }

  // Cancellation: Red tones (failed transaction)
  &.cancellation-event {
    .timeline-content {
      background: lighten($danger, 35%);
      border-left: 3px solid $danger;
    }
  }

  // Return: Yellow/warning tones (reversed transaction)
  &.return-event {
    .timeline-content {
      background: lighten($warning, 35%);
      border-left: 3px solid $warning;
    }
  }
}

.timeline-marker {
  position: absolute;
  left: -30px;
  top: 4px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 1;

  i {
    font-size: 16px;
  }
}

.timeline-content {
  background: $gray-100;
  border-radius: 4px;
  padding: 1rem;
  border-left: 3px solid $gray-300;
}

.event-details-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.event-details-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.event-details-enter-from,
.event-details-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.order-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
